import { Menu, MenuItem, MenuSection } from '../types/menu'
import { menuSchemaScript } from './structuredData'

export interface EmbedConfig {
  showPrices?: boolean
  showDescriptions?: boolean
}

export interface EmbedCode {
  html: string
  css: string
  complete: string
}

export class EmbedGeneratorService {
  generateEmbedCode(menu: Menu, config: EmbedConfig = {}): EmbedCode {
    const {
      showPrices = true,
      showDescriptions = true
    } = config

    const html = this.generateHTML(menu, { showPrices, showDescriptions })
    const css = this.generateCSS(menu, config)
    
    const complete = this.combineCode(html, css)

    return {
      html,
      css,
      complete
    }
  }

  private generateHTML(menu: Menu, options: { showPrices: boolean; showDescriptions: boolean }): string {
    const { showPrices, showDescriptions } = options
    const layout = menu.layout || '1col'
    const isTwoColumn = layout !== '1col'

    const renderSection = (section: MenuSection) => {
      const itemsHTML = section.items
        .map((item: MenuItem) => `
          <div class="menu-item">
            <div class="item-content">
              <h3 class="item-name">${this.escapeHTML(item.name)}</h3>
              ${showDescriptions && item.description ? `
                <p class="item-description">${this.escapeHTML(item.description)}</p>
              ` : ''}
            </div>
            ${showPrices && item.price ? `<span class="item-price">${this.escapeHTML(menu.currency || '')}${this.escapeHTML(String(item.price).replace(/[^\d.]/g, ''))}</span>` : ''}
          </div>
        `).join('')

      return `
        <div class="menu-section">
          <h2 class="section-name">${this.escapeHTML(section.name)}</h2>
          <div class="menu-items">
            ${itemsHTML}
          </div>
        </div>
      `
    }

    // Distribute sections into columns
    const distributeSections = () => {
      if (!isTwoColumn) {
        return [menu.sections]
      }

      const columns: MenuSection[][] = [[], []]

      // Place sections based on their column assignment
      menu.sections.forEach((section) => {
        const column = section.column || 'column1'
        const columnIndex = column === 'column2' ? 1 : 0
        columns[columnIndex].push(section)
      })

      return columns
    }

    const columns = distributeSections()
    const columnsHTML = columns.map((columnSections) => {
      const sectionsHTML = columnSections.map(renderSection).join('')
      return `<div class="menu-column">${sectionsHTML}</div>`
    }).join('')

    const hasItems = menu.sections.some((section) => section.items.length > 0)

    return `
<div class="menu-widget">
  <div class="menu-content">
    ${columnsHTML}
  </div>
</div>
${hasItems ? menuSchemaScript(menu) : ''}
    `.trim()
  }

  private generateCSS(menu: Menu, config: EmbedConfig = {}): string {
    const { colors } = menu.theme
    const { showPrices = true, showDescriptions = true } = config
    const layout = menu.layout || '1col'

    // Determine grid template based on layout
    const getGridTemplate = () => {
      switch (layout) {
        case '2col': return '1fr 1fr'
        case '2col-left': return '3fr 2fr'
        case '2col-right': return '2fr 3fr'
        default: return '1fr'
      }
    }

    // Use individual colors if available, fallback to defaults
    const sectionTitleColor = colors.sectionTitle || colors.primary
    const menuItemTitleColor = colors.menuItemTitle || colors.text
    const descriptionColor = colors.description || colors.secondary
    const priceColor = colors.price || colors.text

    return `
/* Menu Widget Styles */
.menu-widget {
  width: 100%;
  background: transparent;
  padding: 0;
  margin: 0;
}

.menu-widget .menu-content {
  display: grid;
  grid-template-columns: ${getGridTemplate()};
  gap: 96px;
}

.menu-widget .menu-column,
.menu-widget .menu-section,
.menu-widget .menu-items {
  display: flex;
  flex-direction: column;
}

.menu-widget .menu-column { gap: 24px; }
.menu-widget .menu-section { gap: 16px; margin: 0; }
.menu-widget .menu-items { gap: 12px; }
.menu-widget .menu-item { display: flex; align-items: flex-start; gap: 16px; margin: 0; padding: 0; }
.menu-widget .item-content { display: flex; flex-direction: column; max-width: 600px; }

.menu-widget .section-name {
  margin: 0;
  color: ${sectionTitleColor};
}

.menu-widget .item-name {
  margin: 0;
  color: ${menuItemTitleColor};
}

${showDescriptions ? `.menu-widget .item-description {
  margin: 2px 0 0;
  color: ${descriptionColor};
  white-space: pre-line;
}` : ''}

${showPrices ? `.menu-widget .item-price {
  color: ${priceColor};
  white-space: nowrap;
  flex-shrink: 0;
  margin-left: auto;
}` : ''}

/* Responsive */
@media (max-width: 768px) {
  .menu-widget .menu-content { grid-template-columns: 1fr; }
  .menu-widget .menu-item { flex-direction: column; gap: 4px; }
}

@media (max-width: 480px) {
  .menu-widget .menu-content { gap: 20px; }
  .menu-widget .menu-items { gap: 10px; }
}
    `.trim()
  }


  private combineCode(html: string, css: string): string {
    return `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Menu Widget</title>
  <style>
    ${css}
  </style>
</head>
<body>
  ${html}
</body>
</html>
    `.trim()
  }

  private escapeHTML(text: string): string {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }
}

export const embedGenerator = new EmbedGeneratorService()